import { resend2FA } from "@/api/auth";
import InventoZen from "@/components/icons/InventoZen.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { useAuth } from "@/context/auth/AuthContext";
import { getMainLanguage } from "@/i18n";
import { TwoFactorAuthDTO, twoFactorAuthSchema } from "@/lib/auth/auth-types";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { HttpStatusCode } from "axios";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

const TwoFactor = () => {
  const { t } = useTranslation();
  const { twoFactorLogin } = useAuth();
  const navigate = useNavigate();
  const { state } = useLocation();

  const form = useForm<TwoFactorAuthDTO>({
    resolver: zodResolver(twoFactorAuthSchema),
    defaultValues: {
      email: state?.email || "",
      twoFactorToken: "",
      locale: getMainLanguage(),
    },
  });

  const backgroundImageUrl = t("loginBackgroundImageUrl", { defaultValue: "" });

  const handleSubmit = async (data: TwoFactorAuthDTO) => {
    const response = await twoFactorLogin(
      data.email,
      data.twoFactorToken,
      getMainLanguage(),
    );
    if (response.status === HttpStatusCode.Ok) {
      navigate("/instance");
      return;
    }
    if (response.status === HttpStatusCode.Unauthorized) {
      form.setError("root.error", {
        message: t("login.invalid2FAToken"),
      });
      return;
    }
    form.setError("root.error", {
      message: t("login.error"),
    });
  };

  const handleResend = async () => {
    const response = await resend2FA(form.getValues("email"));
    form.resetField("twoFactorToken");
    if (response.status === HttpStatusCode.Gone) {
      navigate("/login", {
        state: { email: form.getValues("email"), twoFactorExpired: true },
      });
      return;
    }
  };

  return (
    <Form {...form}>
      <div className="w-full h-full justify-between items-center inline-flex">
        <div className="w-full h-screen relative">
          <div className="h-full flex items-center justify-center">
            <div className="m-auto w-[409px] h-fit p-10 bg-white rounded-lg shadow border border-slate-200 flex-col items-center gap-8 inline-flex">
              <div>
                <div className="justify-center items-center flex">
                  <InventoZen />
                </div>
                <div className="text-center text-slate-900 text-3xl font-semibold leading-loose">
                  {t("login.twoFactorTitle")}
                </div>
                <div className="text-center text-gray-600 text-sm">
                  {t("login.twoFactorDescription")}
                </div>
              </div>
              <form
                onSubmit={form.handleSubmit(handleSubmit)}
                className="w-full flex flex-col gap-6"
              >
                <div className="flex w-full items-center justify-center">
                  <FormField
                    control={form.control}
                    name="twoFactorToken"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <InputOTP
                            maxLength={6}
                            pattern={REGEXP_ONLY_DIGITS}
                            {...field}
                          >
                            <InputOTPGroup>
                              {[...Array(6)].map((_, index) => (
                                <InputOTPSlot
                                  key={index}
                                  index={index}
                                  className="text-3xl p-7"
                                />
                              ))}
                            </InputOTPGroup>
                          </InputOTP>
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
                {form.formState.errors.root?.error && (
                  <FormMessage>
                    {form.formState.errors.root.error.message}
                  </FormMessage>
                )}
                <Button
                  disabled={!form.formState.isValid}
                  variant="ghost"
                  className="h-12 px-4 py-3 bg-brand-600 rounded-lg border border-brand-700 justify-center items-center gap-1.5 inline-flex"
                >
                  <div className="px-0.5 justify-center items-center flex">
                    <p className="text-white text-base font-semibold">
                      {t("verify")}
                    </p>
                  </div>
                </Button>
                <div className="flex gap-1">
                  <span className="text-center text-gray-600 text-sm">
                    {t("login.twoFactorResendAsk")}
                  </span>
                  <span
                    className="text-center text-brand-600 text-sm hover:underline cursor-pointer"
                    onClick={handleResend}
                  >
                    {t("login.twoFactorResendCTA")}
                  </span>
                </div>
              </form>
            </div>
          </div>
          <div className="absolute bottom-0 w-full h-16 px-12 py-5 bg-white border-t border-slate-200 flex justify-between items-center">
            <div className="text-center text-slate-700 text-sm font-semibold">
              {`${t("copyrightInventoZen")} | ${t("version")}: ${import.meta.env.VITE_CURRENT_GIT_SHA}`}
            </div>
            <div className="gap-5 flex">
              <a href={"#"} className="text-brand-700 text-sm font-bold">
                {t("CGV")}
              </a>
              <div className="text-center text-slate-700 text-sm font-semibold">
                |
              </div>
              <a href={"#"} className="text-brand-700 text-sm font-bold">
                {t("footerLegalNotices")}
              </a>
              <div className="text-center text-slate-700 text-sm font-semibold font-['Raleway'] leading-normal">
                |
              </div>
              <p className="text-[#006eab] text-sm font-bold font-['Raleway'] leading-normal">
                {t("optimizeForChrome")}
              </p>
            </div>
          </div>
        </div>
        <div
          className={cn(
            "h-screen relative md:hidden lg:block transition-all duration-300",
            backgroundImageUrl ? "w-[50%]" : "w-0",
          )}
        >
          {backgroundImageUrl && (
            <img
              src={backgroundImageUrl}
              alt="login"
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute bottom-0 left-0 w-full flex-col justify-center items-center inline-flex">
            <div className="self-stretch p-8 bg-white/10 border-t border-white/10 backdrop-blur-xl flex-col gap-8 flex">
              <div className="h-14 flex-col gap-1 flex">
                <p className="grow shrink basis-0 text-white text-xl font-semibold leading-normal">
                  {t("titleOfTheWork")}
                </p>
                <p className="self-stretch text-white text-lg">
                  {t("artistFirstPage")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Form>
  );
};

export default TwoFactor;
